import { Box, PartnerOnboardingApplication, PartnerOnboardingEntityRequest, Skeleton, SideDrawer } from 'vopay';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BodyText } from 'UI';
import useFetchOnboardingApplication from '@/hooks/useApi/useFetchOnboardingApplication';
import useExtendOnboardingApplication from '@/hooks/useApi/useExtendOnboardingApplication';
import useRemindOnboardingApplication from '@/hooks/useApi/useRemindOnboardingApplication';
import useResendOnboardingEmail from '@/hooks/useApi/useResendOnboardingEmail';
import useOpenOnboardingApplication from '@/hooks/useApi/useOpenOnboardingApplication';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import ApplicationProgress from './ApplicationProgress/ApplicationProgress';
import IdentityCard from './IdentityCard';
import { formatDisplayDate, getApplicationLink, getExtendedExpiryDate, isSameEntity } from './applicationDetails';

export type ApplicationDetailsDrawerProps = {
  /**
   * The entity to show, or `null` when the drawer is closed. The drawer fetches the application itself
   * (`useFetchOnboardingApplication`) whenever this changes, and must not show a previous entity's data
   * while the new one loads.
   */
  entity: PartnerOnboardingEntityRequest | null;
  onClose: () => void;
  /** Extend, Send Reminder or Resend succeeded; the page refetches the table. */
  onChanged: () => void;
  /** Not an Admin (i.e. a Viewer): Extend, Send Reminder, Resend, copy link and open are disabled. */
  isReadOnly: boolean;
  /** The `canSendOnboardingEmail` gate (Inside Sales partners only): Resend is hidden when false. */
  canResendEmail: boolean;
};

/** A fetched application, tagged with the entity it was requested for. */
type LoadedApplication = {
  entity: PartnerOnboardingEntityRequest;
  application: PartnerOnboardingApplication;
};

/** The dates `/extend`, `/remind` and `/resend` return, plus the link status a successful extend implies. */
type ReturnedDates = Partial<
  Pick<PartnerOnboardingApplication, 'ExpiryDate' | 'LastReminderSentDate' | 'SentDate' | 'LinkStatus'>
>;

export default function ApplicationDetailsDrawer({
  entity,
  onClose,
  onChanged,
  isReadOnly,
  canResendEmail,
}: ApplicationDetailsDrawerProps) {
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'OnboardingTracker.ApplicationDetailsDrawer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  // Resend reuses the account page's onboarding-email confirm copy (no Figma for it).
  const { t: tRoot } = useTranslation();
  const toast = useToast();
  const alertDialog = useAlertDialog();

  const { fetchOnboardingApplication } = useFetchOnboardingApplication();
  const { extendingOnboardingApplication, extendOnboardingApplication } = useExtendOnboardingApplication();
  const { remindingOnboardingApplication, remindOnboardingApplication } = useRemindOnboardingApplication();
  const { resendingOnboardingEmail, resendOnboardingEmail } = useResendOnboardingEmail();
  const { openingOnboardingApplication, openOnboardingApplication } = useOpenOnboardingApplication();

  /*
   * The result is kept here, tagged with its entity, rather than read from the hook's
   * `onboardingApplication`: that one holds whichever response landed last and has no request guard, so a
   * slow response for the previous entity could overwrite the current one. Only the latest load stores
   * its result (or its failure), and the card shows only a result for the entity on screen.
   */
  const [loaded, setLoaded] = useState<LoadedApplication | null>(null);
  const [failedEntity, setFailedEntity] = useState<PartnerOnboardingEntityRequest | null>(null);
  // Numbers each load so a response from a superseded load is dropped. The last load started is the one
  // for the entity on screen, because a load starts every time the entity changes.
  const latestLoadIDRef = useRef(0);

  const application = loaded && isSameEntity(loaded.entity, entity) ? loaded.application : null;
  const isLoading = entity !== null && !application && !isSameEntity(failedEntity, entity);
  const link = application ? getApplicationLink(application) : null;

  const isActionPending =
    extendingOnboardingApplication ||
    remindingOnboardingApplication ||
    resendingOnboardingEmail ||
    openingOnboardingApplication;

  useEffect(() => {
    if (entity) loadApplication(entity);
  }, [entity?.EntityType, entity?.EntityID]);

  async function loadApplication(target: PartnerOnboardingEntityRequest) {
    latestLoadIDRef.current += 1;
    const loadID = latestLoadIDRef.current;

    try {
      const res = await fetchOnboardingApplication(target);
      if (loadID !== latestLoadIDRef.current) return;

      setLoaded({ entity: target, application: res });
      setFailedEntity(null);
    } catch (error) {
      if (loadID !== latestLoadIDRef.current) return;

      // A failed refetch after an action keeps the card that is already on screen.
      setFailedEntity(target);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('loadError', { defaultValue: 'Could not load the application details: {{errorMessage}}', errorMessage }),
      );
    }
  }

  function handleClose() {
    // Another entity's card never shows (the entity guard above); this only makes reopening the same
    // entity start from a skeleton rather than the last visit's card.
    setLoaded(null);
    setFailedEntity(null);
    onClose();
  }

  /**
   * After a successful action: the action's returned date goes on the card at once (so it is right even if
   * the refetch fails), then the drawer refetches for everything else and the page refetches the table.
   */
  async function refresh(target: PartnerOnboardingEntityRequest, returnedDates: ReturnedDates) {
    setLoaded((prev) =>
      prev && isSameEntity(prev.entity, target)
        ? { ...prev, application: { ...prev.application, ...returnedDates } }
        : prev,
    );

    onChanged();
    await loadApplication(target);
  }

  async function handleExtendClick() {
    if (!entity || !link) return;

    const target = entity;

    const result = await alertDialog.open(
      t('ExtendDialog.title', { defaultValue: 'Extend Application Expiration Date?' }),
      <BodyText>
        <Trans
          t={t}
          i18nKey="ExtendDialog.body"
          defaults="The expiration date will be extended by <bold>{{count}} days</bold>, and the new expiration date will be <bold>{{date}}</bold>."
          count={link.extendDays}
          values={{
            date: formatDisplayDate(getExtendedExpiryDate(link), i18n.language, { isOrdinal: true }),
          }}
          components={{ bold: <strong /> }}
        />
      </BodyText>,
      {
        abortLabel: tGlobal('Buttons.cancel'),
        confirmLabel: t('ExtendDialog.confirm', { defaultValue: 'Confirm Extension' }),
      },
    );

    if (result !== 'confirm') return;

    let returnedDates: ReturnedDates;

    try {
      const { ExpiryDate } = await extendOnboardingApplication(target);
      // A submitted application can't be extended and the new expiry is in the future, so the link is now
      // active: an expired card must not keep its Inactive tag if the refetch fails.
      returnedDates = { ExpiryDate, LinkStatus: 'Active' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('ExtendDialog.error', { defaultValue: 'Could not extend the application: {{errorMessage}}', errorMessage }),
      );

      return;
    }

    toast.success(t('ExtendDialog.success', { defaultValue: 'Application Extended' }));
    await refresh(target, returnedDates);
  }

  async function handleSendReminderClick() {
    if (!entity || !application) return;

    const target = entity;
    // Judgement call: Figma's "[Customer Name]" is the recipient's full name, which the backend only knows
    // when the primary contact is the recipient. The email stands in otherwise; `Email` is nullable too, so
    // the entity's name is the last resort.
    const recipient = application.Recipient?.FullName ?? application.Recipient?.Email ?? application.Name;

    const result = await alertDialog.open(
      t('ReminderDialog.title', { defaultValue: 'Send Onboarding Reminder?' }),
      t('ReminderDialog.body', {
        defaultValue:
          'A reminder email will be sent to {{recipient}} asking them to complete their onboarding application.',
        recipient,
      }),
      { abortLabel: tGlobal('Buttons.cancel'), confirmLabel: tGlobal('Buttons.send') },
    );

    if (result !== 'confirm') return;

    let returnedDates: ReturnedDates;

    try {
      const { LastReminderSentDate } = await remindOnboardingApplication(target);
      returnedDates = { LastReminderSentDate };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('ReminderDialog.error', { defaultValue: 'Could not send the reminder: {{errorMessage}}', errorMessage }),
      );

      return;
    }

    toast.success(t('ReminderDialog.success', { defaultValue: 'Reminder Sent' }));
    await refresh(target, returnedDates);
  }

  async function handleResendClick() {
    if (!entity || !application) return;

    const target = entity;

    const result = await alertDialog.open(
      tRoot('Accounts.Account.OnboardingEmail.confirmTitle', { defaultValue: 'Send Application Form' }),
      tRoot('Accounts.Account.OnboardingEmail.confirmBody', {
        defaultValue: 'Are you sure you want to send the onboarding email to {{email}}?',
        email: application.Recipient?.Email ?? application.Name,
      }),
      {
        abortLabel: tRoot('Accounts.Account.OnboardingEmail.cancelButton', { defaultValue: 'Cancel' }),
        confirmLabel: tRoot('Accounts.Account.OnboardingEmail.sendButton', { defaultValue: 'Send email' }),
      },
    );

    if (result !== 'confirm') return;

    let returnedDates: ReturnedDates;

    try {
      const { SentDate } = await resendOnboardingEmail(target);
      returnedDates = { SentDate };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('ResendDialog.error', {
          defaultValue: 'Could not send the onboarding email: {{errorMessage}}',
          errorMessage,
        }),
      );

      return;
    }

    // Judgement call: the account page shows nothing on success, so there is no existing copy to reuse.
    toast.success(t('ResendDialog.success', { defaultValue: 'Onboarding email sent' }));
    await refresh(target, returnedDates);
  }

  async function handleOpenClick() {
    if (!entity) return;

    // A tab opened after an await is popup-blocked, so a blank one opens now and is pointed at the
    // OTP-bypass link once it arrives. The link is never rendered, stored or copied.
    const tab = window.open('about:blank', '_blank');

    if (!tab) {
      toast.neutral(
        t('openBlocked', {
          defaultValue: 'Your browser blocked the new tab. Allow pop-ups for this site and try again.',
        }),
      );

      return;
    }

    // Drop the new tab's handle back to the dashboard, so the page loaded there can't redirect the
    // dashboard tab (e.g. to a fake login page). 'noopener' would do this but makes window.open return null.
    tab.opener = null;

    try {
      const res = await openOnboardingApplication(entity);
      tab.location.href = res.OnboardingURL;
    } catch (error) {
      tab.close();
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(t('openError', { defaultValue: 'Could not open the application: {{errorMessage}}', errorMessage }));
    }
  }

  async function handleCopyClick() {
    if (!link?.onboardingURL) return;

    try {
      await navigator.clipboard.writeText(link.onboardingURL);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.neutral(t('copyError', { defaultValue: 'Could not copy the link: {{errorMessage}}', errorMessage }));
    }
  }

  return (
    <SideDrawer
      isOpen={entity !== null}
      onClose={handleClose}
      title={t('title', { defaultValue: 'Application Details' })}
    >
      {renderContent()}
    </SideDrawer>
  );

  function renderContent() {
    if (isLoading) {
      return (
        <Box flx="col" gapSize="16px" height="auto">
          <Skeleton height="291px" borderRadius={16} />
          <Skeleton height="187px" borderRadius={16} />
        </Box>
      );
    }

    // A failed load has already toasted; there is nothing to show.
    if (!application) return null;

    // height="auto": Box defaults to 100%, which pinned the stack to the drawer's height and squeezed
    // the identity card (overflow: hidden) once both cards outgrew it, e.g. on a 1080p laptop (ID-14136).
    // At its natural height the stack scrolls in the drawer instead.
    return (
      <Box flx="col" gapSize="16px" height="auto">
        <IdentityCard
          application={application}
          link={link}
          isReadOnly={isReadOnly}
          canResendEmail={canResendEmail}
          isActionPending={isActionPending}
          onExtendClick={handleExtendClick}
          onOpenClick={handleOpenClick}
          onCopyClick={handleCopyClick}
          onResendClick={handleResendClick}
          onSendReminderClick={handleSendReminderClick}
        />

        {/* Without an application the drawer is the identity card alone (spec §6.5 + plan step 2). */}
        {application.Forms && application.Documents && <ApplicationProgress application={application} />}
      </Box>
    );
  }
}
